import { motion } from "framer-motion";
import { Phone, Mail } from "lucide-react";
import { WhatsAppIcon, WHATSAPP_LINK, PHONE, PEC } from "@/data/brand";
import { useI18n } from "@/i18n";

export const Footer = () => {
  const { t } = useI18n();
  const f = t.footer;

  return (
    <footer
      id="footer"
      data-testid="footer"
      className="relative border-t border-white/[0.08] overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(80%_120%_at_50%_100%,rgba(37,99,235,0.16),transparent_70%)]" />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-28 lg:py-36">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.35em] text-white/40 mb-8">
            {f.brandKicker}
          </p>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1] max-w-4xl">
            <span className="text-white">{f.headline1}</span>
            <br />
            <span className="text-gradient">{f.headline2}</span>
          </h2>
        </motion.div>

        <div className="mt-20 grid md:grid-cols-3 gap-10 border-t border-white/[0.08] pt-14">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">
              {f.byLabel}
            </p>
            <p className="font-display text-xl text-white leading-snug">
              {f.firm}
              <span className="block text-white/50 text-base font-normal">
                {f.firmSub}
              </span>
            </p>
          </div>

          <div className="space-y-5">
            <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">
              {f.contactsLabel}
            </p>
            <a
              href="tel:+393499592959"
              data-testid="footer-phone"
              className="flex items-center gap-3 text-white/70 hover:text-white transition-colors duration-300"
            >
              <Phone className="w-4 h-4 text-blue-400" strokeWidth={1.5} />
              {PHONE}
            </a>
            <a
              href={`mailto:${PEC}`}
              data-testid="footer-pec"
              className="flex items-center gap-3 text-white/70 hover:text-white transition-colors duration-300 break-all"
            >
              <Mail className="w-4 h-4 text-blue-400" strokeWidth={1.5} />
              {PEC}
              <span className="text-[10px] uppercase tracking-widest text-white/30">
                {f.pec}
              </span>
            </a>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">
              {f.assistLabel}
            </p>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat on WhatsApp"
              data-testid="footer-whatsapp"
              className="group inline-flex items-center gap-3 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-black hover:bg-[#20bd5a] transition-colors duration-300"
            >
              <WhatsAppIcon className="w-5 h-5" />
              {f.whatsapp}
            </a>
          </div>
        </div>

        <div className="mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-white/[0.08] pt-8">
          <p className="text-xs text-white/35">
            © {new Date().getFullYear()} {f.rights}
          </p>
          <p className="text-xs text-white/35">{f.tagline}</p>
        </div>
      </div>
    </footer>
  );
};
